import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Trophy, Mail, RotateCcw, Sparkles, Loader2 } from 'lucide-react';
import ResultsTable from '../components/ResultsTable';
import { getResults } from '../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Alert, AlertDescription } from '../components/ui/alert';

function ResultsPage() {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchResults = async () => {
      try {
        setLoading(true);
        const data = await getResults(jobId);
        setResults(data);
        setError(null);
      } catch (err) {
        setError(err.response?.data?.error || err.message || 'Failed to load results');
      } finally {
        setLoading(false);
      }
    };

    if (jobId) {
      fetchResults();
    }
  }, [jobId]);

  const handleBack = () => {
    navigate('/');
  };

  const handleEmail = () => {
    navigate('/email');
  };

  if (loading) {
    return (
      <div className="min-h-screen">
        <header className="bg-gradient-to-r from-emerald-600 to-teal-600 text-white shadow-lg">
          <div className="container mx-auto px-4 py-8">
            <div className="flex items-center justify-center gap-3">
              <Sparkles className="h-8 w-8" />
              <h1 className="text-3xl font-bold tracking-tight">Resume Screener</h1>
            </div>
          </div>
        </header>
        <main className="container mx-auto px-4 py-8 max-w-6xl">
          <Card className="shadow-xl border-0">
            <CardContent className="flex items-center justify-center py-12">
              <div className="flex items-center gap-3 text-muted-foreground">
                <Loader2 className="h-6 w-6 animate-spin" />
                <span>Loading results...</span>
              </div>
            </CardContent>
          </Card>
        </main>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen">
        <header className="bg-gradient-to-r from-emerald-600 to-teal-600 text-white shadow-lg">
          <div className="container mx-auto px-4 py-8">
            <div className="flex items-center justify-center gap-3">
              <Sparkles className="h-8 w-8" />
              <h1 className="text-3xl font-bold tracking-tight">Resume Screener</h1>
            </div>
          </div>
        </header>
        <main className="container mx-auto px-4 py-8 max-w-6xl">
          <Card className="shadow-xl border-0">
            <CardContent className="py-8 space-y-4">
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
              <Button onClick={handleBack} className="gap-2">
                <RotateCcw className="h-4 w-4" />
                Back to Home
              </Button>
            </CardContent>
          </Card>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      {/* Header */}
      <header className="bg-gradient-to-r from-emerald-600 to-teal-600 text-white shadow-lg">
        <div className="container mx-auto px-4 py-8">
          <div className="flex items-center justify-center gap-3">
            <Trophy className="h-8 w-8" />
            <h1 className="text-3xl font-bold tracking-tight">Resume Screening Results</h1>
          </div>
        </div>
      </header>
      
      {/* Main Content */}
      <main className="container mx-auto px-4 py-8 max-w-6xl">
        <Card className="shadow-xl border-0 animate-fade-in">
          <CardHeader className="pb-4">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <CardTitle className="text-xl flex items-center gap-2">
                <Trophy className="h-5 w-5 text-amber-500" />
                Ranked Candidates
              </CardTitle>
              <div className="flex gap-2">
                <Button onClick={handleEmail} className="gap-2 bg-emerald-600 hover:bg-emerald-700">
                  <Mail className="h-4 w-4" />
                  Send Emails
                </Button>
                <Button onClick={handleBack} variant="outline" className="gap-2">
                  <RotateCcw className="h-4 w-4" />
                  Run Another Job
                </Button>
              </div>
            </div>
          </CardHeader>
          
          <CardContent>
            {results.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                No results found. The job may still be processing.
              </div>
            ) : (
              <ResultsTable results={results} />
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
}

export default ResultsPage;
